import * as Sentry from '@sentry/react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { App } from './components/App/App';
import { ClientRouter } from './components/ClientRouter';
import { Config } from './config';
import { onTemporaryFailure, withoutQuery } from './state/api';
import { reportInp } from './utils/webVitals';

const config = getData<Config>('config-data');
type QueuedInlineScriptError = {
	message: string;
	stack?: string;
	origin: string;
};
const reactErrorCodePattern = /Minified React error #(\d+)/i;
const sentryRelease = config.version ? `kg-front@${config.version}` : undefined;
const sentryReplayEnabled = config.sentryReplayEnabled ?? !import.meta.env.DEV;
const windowWithInlineScriptErrorCapture = window as Window & {
	__kgInlineScriptErrors?: QueuedInlineScriptError[];
	__kgCaptureInlineScriptError?: (error: unknown, origin: string) => void;
};

function captureInlineScriptError(error: unknown, origin: string) {
	if (!config.sentryDsn) return;

	Sentry.withScope((scope) => {
		scope.setTag('origin', 'inline_script');
		scope.setTag('inline_script_origin', origin);
		Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
	});
}

if (config.sentryDsn) {
	Sentry.init({
		dsn: config.sentryDsn,
		environment: import.meta.env.MODE,
		release: sentryRelease,
		maxBreadcrumbs: 100,
		normalizeDepth: 6,
		integrations: sentryReplayEnabled ? [Sentry.replayIntegration()] : [],
		/*
		 * Http breadcrumbs carry the url each request went to, and several endpoints carry the
		 * customer's own words in the query string - an email address, a company name, a street,
		 * a search term. Up to a hundred of those ride along with every event, so redacting the
		 * url a report is *about* achieves nothing while the history beside it is untouched.
		 */
		beforeBreadcrumb(breadcrumb) {
			const url = breadcrumb.data?.url;
			if ((breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') && typeof url === 'string') {
				breadcrumb.data = { ...breadcrumb.data, url: withoutQuery(url) };
			}
			return breadcrumb;
		},
		replaysSessionSampleRate: sentryReplayEnabled ? 1.0 : 0,
		// replaysSessionSampleRate: sentryReplayEnabled ? (import.meta.env.DEV ? 1.0 : 0.1) : 0,
		replaysOnErrorSampleRate: sentryReplayEnabled ? 1.0 : 0,
		beforeSend(event, hint) {
			const { href, pathname, hash } = window.location;
			const originalException = hint.originalException;
			const errorMessage =
				typeof originalException === 'string'
					? originalException
					: originalException instanceof Error
						? originalException.message
						: event.exception?.values?.[0]?.value ?? '';
			const reactErrorMatch = errorMessage.match(reactErrorCodePattern);
			const reactErrorCode = reactErrorMatch?.[1];

			event.tags = {
				...event.tags,
				store: config.store,
				domain: config.domain,
				...(reactErrorCode ? { react_error_code: reactErrorCode } : {}),
			};

			/*
			 * The HttpContext integration fills these in from window.location and the referrer
			 * before this runs, independently of the context below - so redacting only our own
			 * copy leaves the sdk's untouched in the very same event.
			 */
			if (event.request?.url) event.request.url = withoutQuery(event.request.url);
			const headers = event.request?.headers;
			if (headers) {
				for (const name of ['Referer', 'referer']) {
					const value = headers[name];
					if (typeof value === 'string') headers[name] = withoutQuery(value);
				}
			}

			event.contexts = {
				...event.contexts,
				app_config: {
					store: config.store,
					baseUrl: config.baseUrl,
					domain: config.domain,
					locale: config.locale,
					country: config.country,
					version: config.version ?? 'unknown',
				},
				/*
				 * Without the query string, here too: `?q=` on search and `?sid=` at checkout are
				 * the customer's, and the path already says which page they were on. The referrer
				 * gets the same treatment, since it is somebody's previous url.
				 */
				location: {
					href: withoutQuery(href),
					pathname,
					hash,
					referrer: document.referrer ? withoutQuery(document.referrer) : undefined,
				},
				...(reactErrorCode
					? {
							react_error: {
								code: reactErrorCode,
								decoderUrl: `https://reactjs.org/docs/error-decoder.html?invariant=${reactErrorCode}`,
							},
						}
					: {}),
			};

			return event;
		},
	});
	Sentry.setTag('store', config.store);
	Sentry.setTag('domain', config.domain);
	Sentry.setContext('app_config', {
		store: config.store,
		baseUrl: config.baseUrl,
		domain: config.domain,
		locale: config.locale,
		country: config.country,
		version: config.version ?? 'unknown',
	});
	windowWithInlineScriptErrorCapture.__kgCaptureInlineScriptError = captureInlineScriptError;
	windowWithInlineScriptErrorCapture.__kgInlineScriptErrors?.forEach(({ message, stack, origin }) => {
		const error = new Error(message);
		if (stack) error.stack = stack;
		captureInlineScriptError(error, origin);
	});
	delete windowWithInlineScriptErrorCapture.__kgInlineScriptErrors;
}

/*
 * Temporary upstream failures, reported as warnings rather than exceptions: none of these is a
 * fault in the page, and treating them as crashes would bury the ones that are.
 *
 * Grouped by resource rather than by url. A rate-limited cms would otherwise open one issue per
 * product page, and the question worth answering is which upstream is struggling, not which
 * visitor happened to be reading at the time.
 */
onTemporaryFailure(({ url, status, kind, resource, upstream }) => {
	if (!config.sentryDsn) return;

	// the fetch layer cannot name a resource, so the host stands in - it is what separates the
	// cms from the api from Loop54, which is the distinction worth grouping by
	const grouping = resource ?? upstream;

	Sentry.withScope((scope) => {
		scope.setLevel('warning');
		scope.setTag('origin', 'temporary_failure');
		scope.setTag('failure_kind', kind);
		scope.setTag('failure_status', String(status));
		scope.setTag('failure_upstream', upstream);
		if (resource) scope.setTag('failure_resource', resource);
		scope.setContext('temporary_failure', { url, status, kind, resource: resource ?? null, upstream });
		scope.setFingerprint(['temporary-failure', kind, grouping, String(status)]);
		Sentry.captureMessage(`temporary failure (${kind}): ${grouping} — ${status}`, 'warning');
	});
});

const app = (
	<ClientRouter>
		<HelmetProvider>
			<App config={config} preloadedData={getData('preloaded-data')} localization={getData('localization')} />
		</HelmetProvider>
	</ClientRouter>
);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root element.');

const onRecoverableError = (error: unknown, errorInfo: { componentStack?: string }) => {
	if (!config.sentryDsn) return;

	Sentry.captureException(error, {
		tags: {
			origin: 'hydrateRoot.onRecoverableError',
		},
		extra: {
			componentStack: errorInfo.componentStack,
		},
	});
};

if (rootElement.hasChildNodes()) {
	hydrateRoot(rootElement, app, { onRecoverableError });
} else {
	createRoot(rootElement).render(app);
}

// search console says which pages are slow to respond; this says which interaction on them is
reportInp();

function getData<T>(id: string): T {
	try {
		return JSON.parse(window.document.getElementById(id)?.innerHTML!);
	} catch (e) {
		return {} as T;
	}
}
